import { useState, type ComponentType, type ReactNode } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { AlertCircle, X, Upload, Camera, User, FileText, CalendarDays, Trash2, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { DateInput } from '@/components/DateInput';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ImagePreview } from '@/components/ImagePreview';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { getApiErrorMessage } from '@/api/errorHandler';
import { UserAPIRequestMapper, UserAPIResponseMapper } from '@/api/mapper/user';
import { createUser, updateUser } from '@/api/service/user';
import { uploadFile, uploadProfilePhoto } from '@/api/service/upload';
import { useMemberContext } from '@/contexts/MemberContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { EDUCATION_QUALIFICATION_OPTIONS } from '@/lib/constants';
import { formatFileName, getEducationQualificationLabel } from '@/lib/utils';
import type { Gender } from '@/api/type/user';
import type { UpsertMemberProps, MemberFormData } from '@/types';

const ADD_MEMBER_FORM_ID = 'add-member-form';
const EDIT_MEMBER_FORM_ID = 'edit-member-form';

const INDIAN_MOBILE_PATTERN = /^[6-9]\d{9}$/;
const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

type UploadSlot = { name?: string; url?: string; uploading: boolean; error?: string };
const EMPTY_SLOT: UploadSlot = { uploading: false };

const resetFileInput = (inputId: string) => {
  const input = document.getElementById(inputId) as HTMLInputElement | null;
  if (input) input.value = '';
};

const toLettersOnly = (value: string) => value.replace(/[^\p{L}\s]/gu, '');

const normalizeIndianPhoneDigits = (value: string) => value.replace(/\D/g, '').slice(0, 10);

const parseDateValue = (value?: string) => {
  if (!value) return null;

  const dm = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (dm) {
    const dd = Number(dm[1]);
    const mm = Number(dm[2]);
    const yyyy = Number(dm[3]);
    if (!dd || !mm || !yyyy) return null;
    const date = new Date(yyyy, mm - 1, dd);
    if (Number.isNaN(date.getTime())) return null;
    if (date.getFullYear() !== yyyy || date.getMonth() !== mm - 1 || date.getDate() !== dd) return null;
    return date;
  }

  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    const yyyy = Number(iso[1]);
    const mm = Number(iso[2]);
    const dd = Number(iso[3]);
    const date = new Date(yyyy, mm - 1, dd);
    if (Number.isNaN(date.getTime())) return null;
    if (date.getFullYear() !== yyyy || date.getMonth() !== mm - 1 || date.getDate() !== dd) return null;
    return date;
  }

  return null;
};

const dateToDisplay = (value?: string) => {
  const parsed = parseDateValue(value);
  if (!parsed) return value || '';
  const dd = String(parsed.getDate()).padStart(2, '0');
  const mm = String(parsed.getMonth() + 1).padStart(2, '0');
  const yyyy = parsed.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const dateToIso = (value?: string) => {
  const parsed = parseDateValue(value);
  if (!parsed) return value || '';
  const yyyy = parsed.getFullYear();
  const mm = String(parsed.getMonth() + 1).padStart(2, '0');
  const dd = String(parsed.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const calcEndDateDisplay = (joinedDate: string) => {
  const joinDate = parseDateValue(joinedDate);
  if (!joinDate) return '';
  const endDate = new Date(joinDate);
  endDate.setMonth(endDate.getMonth() + 100);
  const dd = String(endDate.getDate()).padStart(2, '0');
  const mm = String(endDate.getMonth() + 1).padStart(2, '0');
  const yyyy = endDate.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const todayIso = () => new Date().toISOString().split('T')[0];

const calcAgeFromDob = (dob?: string) => {
  const dobDate = parseDateValue(dob);
  if (!dobDate) return '';

  const today = new Date();
  let age = today.getFullYear() - dobDate.getFullYear();
  const m = today.getMonth() - dobDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dobDate.getDate())) {
    age -= 1;
  }
  return age >= 0 ? String(age) : '';
};

const DELETE_BUTTON_CLASS =
  'border-[#dc143c]/25 bg-[#dc143c]/10 text-[#dc143c] hover:bg-[#dc143c]/15 hover:text-[#b01030] hover:border-[#dc143c]/35';

const DRAWER_CLASS =
  'h-full !w-full !min-w-0 !max-w-full overflow-hidden flex flex-col sm:!w-[45vw] sm:!min-w-[650px] sm:!max-w-[45vw]';

const FIELD_CLASS =
  'h-10 box-border border-gray-200 bg-white transition-colors focus-visible:border-[#4369a0] focus-visible:ring-1 focus-visible:ring-[#4369a0]/25';

const FIELD_WRAP_CLASS =
  'flex h-10 box-border overflow-hidden rounded-sm border border-gray-200 bg-white transition-colors focus-within:border-[#4369a0] focus-within:ring-1 focus-within:ring-[#4369a0]/25';

const SELECT_FIELD_CLASS =
  '!h-10 box-border !py-0 rounded-sm border-gray-200 !bg-white text-gray-900 shadow-none transition-colors hover:!bg-white focus-visible:border-[#4369a0] focus-visible:ring-1 focus-visible:ring-[#4369a0]/25 [&_[data-placeholder]]:text-muted-foreground';

const READONLY_FIELD_CLASS = 'h-10 box-border border-gray-200 bg-gray-50 text-gray-600';

const LABEL_CLASS = 'flex min-h-5 items-center text-gray-700';

const NOMINEE_RELATIONSHIP_OPTIONS = [
  'Father',
  'Mother',
  'Spouse',
  'Son',
  'Daughter',
  'Brother',
  'Sister',
  'Other',
] as const;

type MemberFormValues = {
  name: string;
  fatherOrHusbandName: string;
  gender: string;
  dob: string;
  educationQualification: string;
  occupation: string;
  phone: string;
  email: string;
  address: string;
  joinedDate: string;
  endDate: string;
  nomineeName: string;
  nomineeRelationship: string;
  lifeStatus: 'alive' | 'deceased';
};

function FieldError({ show, message }: { show: boolean; message?: string }) {
  if (!show || !message) return null;
  return <p className="!mt-0 block text-[13px] leading-4 text-[crimson]">{message}</p>;
}

function FormSection({
  icon: Icon,
  title,
  description,
  headerAside,
  children,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  description?: string;
  headerAside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-xs">
      <div className="border-b border-gray-100 px-5 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              <Icon className="h-4 w-4 text-[#4369a0]" />
              {title}
            </h3>
            {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
          </div>
          {headerAside}
        </div>
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

export function UpsertMember({ member, open, onOpenChange }: UpsertMemberProps) {
  const { addMember, updateMember } = useMemberContext();
  const isAddMode = !member;
  const formId = isAddMode ? ADD_MEMBER_FORM_ID : EDIT_MEMBER_FORM_ID;
  const profileImageInputId = `${formId}-profile-image`;
  const proof1InputId = `${formId}-proof-1`;
  const proof2InputId = `${formId}-proof-2`;

  const [photoSlot, setPhotoSlot] = useState<UploadSlot>(EMPTY_SLOT);
  const [proof1Slot, setProof1Slot] = useState<UploadSlot>(EMPTY_SLOT);
  const [proof2Slot, setProof2Slot] = useState<UploadSlot>(EMPTY_SLOT);
  const [photoRemoved, setPhotoRemoved] = useState(false);
  const isUploading = photoSlot.uploading || proof1Slot.uploading || proof2Slot.uploading;
  const existingPhotoUrl = (member?.profileImage || member?.photo || '').trim();
  // Photo currently shown: a fresh upload, else the saved one (unless the user removed it).
  const currentPhotoUrl = photoSlot.url || (photoRemoved ? '' : existingPhotoUrl);

  const resetUploads = () => {
    setPhotoSlot(EMPTY_SLOT);
    setPhotoRemoved(false);
    setProof1Slot(EMPTY_SLOT);
    setProof2Slot(EMPTY_SLOT);
    resetFileInput(profileImageInputId);
    resetFileInput(proof1InputId);
    resetFileInput(proof2InputId);
  };

  // Upload as soon as a file is picked; the save API only receives the returned URL.
  const handleUpload = async (
    file: File,
    setSlot: (slot: UploadSlot) => void,
    upload: (file: File) => Promise<{ url: string }>,
    inputId: string
  ) => {
    setSlot({ name: file.name, uploading: true });
    try {
      const { url } = await upload(file);
      setSlot({ name: file.name, url, uploading: false });
    } catch (error) {
      console.error(error);
      setSlot({ uploading: false, error: getApiErrorMessage(error) });
      resetFileInput(inputId);
    }
  };

  const formik = useFormik<MemberFormValues>({
    enableReinitialize: true,
    initialValues: {
      name: member?.name || '',
      fatherOrHusbandName: member?.fatherOrHusbandName || '',
      gender: member?.gender?.toLowerCase() || '',
      dob: dateToDisplay(member?.dob) || '',
      educationQualification: getEducationQualificationLabel(member?.educationQualification) ?? '',
      occupation: member?.occupation || '',
      phone: normalizeIndianPhoneDigits(member?.phone || ''),
      email: member?.email || '',
      address: member?.address || '',
      joinedDate: dateToDisplay(member?.joinedDate) || dateToDisplay(todayIso()),
      endDate:
        dateToDisplay(member?.endDate) ||
        calcEndDateDisplay(dateToDisplay(member?.joinedDate) || dateToDisplay(todayIso())),
      nomineeName: member?.nomineeName || '',
      nomineeRelationship: member?.nomineeRelationship || '',
      lifeStatus: member?.lifeStatus === 'deceased' ? 'deceased' : 'alive',
    },
    validationSchema: yup.object({
      name: yup.string().trim().required('Please enter member full name'),
      fatherOrHusbandName: yup.string().trim().required('Please enter father/husband name'),
      gender: yup.string().required('Please select gender'),
      dob: yup
        .string()
        .required('Please enter date of birth')
        .test('dob-format', 'Please enter date of birth in dd/mm/yyyy format', (value) =>
          Boolean(parseDateValue(value))
        ),
      educationQualification: yup.string(),
      occupation: yup.string(),
      phone: yup
        .string()
        .required('Please enter mobile number')
        .matches(INDIAN_MOBILE_PATTERN, 'Please enter a valid Indian mobile number'),
      email: yup
        .string()
        .trim()
        .test('email-format', 'Please enter a valid email address', (value) => !value || EMAIL_PATTERN.test(value)),
      address: yup.string(),
      joinedDate: yup
        .string()
        .required('Please enter joined date')
        .test('joined-format', 'Please enter joined date in dd/mm/yyyy format', (value) =>
          Boolean(parseDateValue(value))
        ),
      endDate: yup.string(),
      nomineeName: yup.string().trim().required('Please enter nominee name'),
      nomineeRelationship: yup.string().required('Please select nominee relationship'),
      lifeStatus: yup.mixed<'alive' | 'deceased'>().oneOf(['alive', 'deceased']),
    }),
    onSubmit: async (values, { setStatus, resetForm }) => {
      setStatus(undefined);
      const ageValue = calcAgeFromDob(values.dob);
      const payload: MemberFormData = {
        name: values.name.trim(),
        fatherOrHusbandName: values.fatherOrHusbandName.trim(),
        gender: values.gender as Gender,
        dob: dateToIso(values.dob),
        age: ageValue ? Number(ageValue) : undefined,
        educationQualification: values.educationQualification || undefined,
        occupation: values.occupation.trim(),
        phone: normalizeIndianPhoneDigits(values.phone),
        email: values.email.trim(),
        address: values.address.trim(),
        joinedDate: dateToIso(values.joinedDate),
        endDate: dateToIso(values.endDate),
        nomineeName: values.nomineeName.trim(),
        nomineeRelationship: values.nomineeRelationship,
        lifeStatus: values.lifeStatus,
      };

      // New upload -> its URL; removed saved photo -> '' to clear it; otherwise leave unchanged.
      payload.photo = photoSlot.url ?? (photoRemoved && existingPhotoUrl ? '' : undefined);
      payload.proof1 = proof1Slot.url ? proof1Slot.name : member?.proof1;
      payload.proof1Url = proof1Slot.url ?? member?.proof1Url;
      payload.proof2 = proof2Slot.url ? proof2Slot.name : member?.proof2;
      payload.proof2Url = proof2Slot.url ?? member?.proof2Url;

      try {

        if (isAddMode) {
          const created = await createUser(UserAPIRequestMapper(payload));
          addMember(UserAPIResponseMapper(created));
        } else {
          const updated = await updateUser(Number(member!.id), UserAPIRequestMapper(payload));
          updateMember(member!.id, UserAPIResponseMapper(updated));
        }
        resetForm();
        resetUploads();
        onOpenChange(false);
      } catch (error) {
        console.error(error);
        setStatus(getApiErrorMessage(error));
      }
    },
  });

  const ageValue = calcAgeFromDob(formik.values.dob);
  const showFieldError = (name: keyof MemberFormValues) =>
    Boolean((formik.touched[name] || formik.submitCount > 0) && formik.errors[name]);

  const handleClose = () => {
    if (formik.isSubmitting) return;
    formik.resetForm();
    resetUploads();
    onOpenChange(false);
  };

  const setSelectValue = (field: keyof MemberFormValues, value: string) => {
    formik.setFieldValue(field, value);
    formik.setFieldTouched(field, true, false);
  };

  return (
    <Drawer
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          onOpenChange(true);
          return;
        }
        if (formik.isSubmitting) return;
        formik.resetForm();
        resetUploads();
        onOpenChange(false);
      }}
      direction="right"
    >
      <DrawerContent className={DRAWER_CLASS}>
        <DrawerHeader className="shrink-0 border-b border-gray-200 bg-white px-5 py-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <DrawerTitle className="text-base font-semibold text-gray-900">
                {isAddMode ? 'Add Member' : 'Edit Member'}
              </DrawerTitle>
              {isAddMode ? (
                <p className="mt-0.5 text-sm text-gray-500">Create a new member profile</p>
              ) : (
                <p className="truncate text-sm font-medium tabular-nums text-[#4369a0]">
                  #{member?.id} - {member?.name}
                </p>
              )}
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <DrawerClose asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0 rounded-md border border-gray-200 text-gray-500 hover:border-[#4369a0]/30 hover:bg-[#4369a0]/10 hover:text-[#4369a0]"
                  >
                    <X className="h-3.5 w-3.5" />
                    <span className="sr-only">Close</span>
                  </Button>
                </DrawerClose>
              </TooltipTrigger>
              <TooltipContent side="bottom">Close</TooltipContent>
            </Tooltip>
          </div>
        </DrawerHeader>

        <div className="min-h-0 flex-1 overflow-y-auto scrollbar-hide bg-gray-50/80 px-6 py-5">
          {(isAddMode || member) && (
            <form id={formId} noValidate onSubmit={formik.handleSubmit} className="space-y-5">
              <FormSection icon={Camera} title="Profile Photo" description="Upload a clear photo for the member profile">
                <div className="flex items-center gap-5">
                  <div className="relative">
                  <ImagePreview src={currentPhotoUrl} alt={formik.values.name || 'Member photo'}>
                  <Avatar className="h-20 w-20 border-2 border-white shadow-xs ring-1 ring-gray-200/60">
                    <AvatarImage src={currentPhotoUrl} />
                    <AvatarFallback className="bg-[#4369a0]/10 text-lg font-semibold text-[#4369a0]">
                      {formik.values.name
                        ? formik.values.name
                            .split(' ')
                            .map((n) => n[0])
                            .join('')
                        : 'U'}
                    </AvatarFallback>
                  </Avatar>
                  </ImagePreview>
                    {photoSlot.uploading && (
                      <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40">
                        <span className="h-6 w-6 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 space-y-2">
                    <input
                      type="file"
                      accept="image/*"
                      disabled={photoSlot.uploading}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleUpload(file, setPhotoSlot, uploadProfilePhoto, profileImageInputId);
                      }}
                      className="hidden"
                      id={profileImageInputId}
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      <label htmlFor={profileImageInputId} className="cursor-pointer">
                        <Button type="button" variant="outline" size="sm" className="border-gray-200 bg-white" asChild>
                          <span>
                            <Camera className="mr-2 h-4 w-4" />
                            {photoSlot.uploading ? 'Uploading...' : currentPhotoUrl ? 'Change Photo' : 'Choose Photo'}
                          </span>
                        </Button>
                      </label>
                      {currentPhotoUrl && !photoSlot.uploading && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className={DELETE_BUTTON_CLASS}
                          onClick={() => {
                            setPhotoSlot(EMPTY_SLOT);
                            setPhotoRemoved(true);
                            resetFileInput(profileImageInputId);
                          }}
                        >
                          <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                          Remove
                        </Button>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">JPG, PNG or WEBP up to 5MB</p>
                    {photoSlot.error && <p className="text-sm text-red-600">{photoSlot.error}</p>}
                  </div>
                </div>
              </FormSection>

              <FormSection
                icon={User}
                title="Member Details"
                description="Personal and identity information"
                headerAside={
                  isAddMode ? undefined : (
                    <div className="w-[168px] shrink-0 space-y-1.5">
                      <Label htmlFor={`${formId}-life-status`} className="text-gray-700">
                        Life Status
                      </Label>
                      <Select
                        value={formik.values.lifeStatus}
                        onValueChange={(value: string) => setSelectValue('lifeStatus', value)}
                      >
                        <SelectTrigger id={`${formId}-life-status`} className={SELECT_FIELD_CLASS}>
                          <SelectValue placeholder="Select life status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="alive">Alive</SelectItem>
                          <SelectItem value="deceased">Deceased</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )
                }
              >
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor={`${formId}-name`} className={LABEL_CLASS}>
                      Member Full Name *
                    </Label>
                    <Input
                      id={`${formId}-name`}
                      name="name"
                      value={formik.values.name}
                      onChange={(e) => formik.setFieldValue('name', toLettersOnly(e.target.value))}
                      onBlur={formik.handleBlur}
                      placeholder="Enter full name"
                      className={FIELD_CLASS}
                    />
                    <FieldError show={showFieldError('name')} message={formik.errors.name} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`${formId}-father-or-husband-name`} className={LABEL_CLASS}>
                      Father/Husband Name *
                    </Label>
                    <Input
                      id={`${formId}-father-or-husband-name`}
                      name="fatherOrHusbandName"
                      value={formik.values.fatherOrHusbandName}
                      onChange={(e) =>
                        formik.setFieldValue('fatherOrHusbandName', toLettersOnly(e.target.value))
                      }
                      onBlur={formik.handleBlur}
                      placeholder="Enter father/husband name"
                      className={FIELD_CLASS}
                    />
                    <FieldError
                      show={showFieldError('fatherOrHusbandName')}
                      message={formik.errors.fatherOrHusbandName}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`${formId}-gender`} className={LABEL_CLASS}>
                      Gender *
                    </Label>
                    <Select
                      value={formik.values.gender}
                      onValueChange={(value: string) => setSelectValue('gender', value)}
                    >
                      <SelectTrigger id={`${formId}-gender`} className={SELECT_FIELD_CLASS}>
                        <SelectValue
                          placeholder="Select gender"
                          className="text-gray-900 data-[placeholder]:text-muted-foreground"
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="others">Others</SelectItem>
                      </SelectContent>
                    </Select>
                    <FieldError show={showFieldError('gender')} message={formik.errors.gender} />
                  </div>

                  <div className="space-y-2">
                    <div className="flex min-h-5 items-center gap-2">
                      <Label htmlFor={`${formId}-dob`} className={LABEL_CLASS}>
                        Date Of Birth *
                      </Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex items-center text-gray-400 hover:text-gray-600"
                          >
                            <Info className="h-4 w-4" />
                            <span className="sr-only">Date of birth format info</span>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                          Date of birth should be dd/mm/yyyy format. eg: 25/12/1998
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <DateInput
                      id={`${formId}-dob`}
                      name="dob"
                      value={formik.values.dob}
                      onChange={(value) => formik.setFieldValue('dob', value)}
                      onBlur={formik.handleBlur}
                    />
                    <FieldError show={showFieldError('dob')} message={formik.errors.dob} />
                  </div>

                  <div className="space-y-2">
                    <Label className={LABEL_CLASS}>Age (Auto-calculated)</Label>
                    <Input
                      value={ageValue || '--'}
                      readOnly
                      className={READONLY_FIELD_CLASS}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`${formId}-education`} className={LABEL_CLASS}>
                      Education qualification
                    </Label>
                    <Select
                      value={formik.values.educationQualification || undefined}
                      onValueChange={(value: string) => {
                        formik.setFieldValue('educationQualification', value);
                        formik.setFieldTouched('educationQualification', true, false);
                      }}
                    >
                      <SelectTrigger id={`${formId}-education`} className={SELECT_FIELD_CLASS}>
                        <SelectValue
                          placeholder="Select qualification"
                          className="text-gray-900 data-[placeholder]:text-muted-foreground"
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {EDUCATION_QUALIFICATION_OPTIONS.map((option) => (
                          <SelectItem key={option.id} value={option.label}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor={`${formId}-occupation`} className={LABEL_CLASS}>
                      Occupation
                    </Label>
                    <Input
                      id={`${formId}-occupation`}
                      name="occupation"
                      value={formik.values.occupation}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={FIELD_CLASS}
                    />
                  </div>
                </div>
              </FormSection>

              <FormSection
                icon={CalendarDays}
                title="Membership Dates"
                description="End date is auto-calculated as 100 months from the joined date"
              >
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <div className="flex min-h-5 items-center gap-2">
                      <Label htmlFor={`${formId}-joined-date`} className={LABEL_CLASS}>
                        Joined Date *
                      </Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex items-center text-gray-400 hover:text-gray-600"
                          >
                            <Info className="h-4 w-4" />
                            <span className="sr-only">Joined date format info</span>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                          Joined date should be dd/mm/yyyy format. eg: 25/12/1998
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <DateInput
                      id={`${formId}-joined-date`}
                      name="joinedDate"
                      value={formik.values.joinedDate}
                      onChange={(value) => {
                        formik.setFieldValue('joinedDate', value);
                        formik.setFieldValue(
                          'endDate',
                          parseDateValue(value) ? calcEndDateDisplay(value) : ''
                        );
                      }}
                      onBlur={formik.handleBlur}
                    />
                    <FieldError show={showFieldError('joinedDate')} message={formik.errors.joinedDate} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`${formId}-end-date`} className={LABEL_CLASS}>
                      End Date (Auto-calculated)
                    </Label>
                    <Input
                      id={`${formId}-end-date`}
                      value={formik.values.endDate || '--'}
                      readOnly
                      className={READONLY_FIELD_CLASS}
                    />
                  </div>
                </div>
              </FormSection>

              <FormSection icon={User} title="Contact" description="Indian phone number and optional contact details">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor={`${formId}-phone`} className={LABEL_CLASS}>
                      Phone Number *
                    </Label>
                    <div className={FIELD_WRAP_CLASS}>
                      <span className="flex h-full items-center bg-gray-50 px-3 text-sm font-medium text-gray-600">+91</span>
                      <input
                        id={`${formId}-phone`}
                        name="phone"
                        value={formik.values.phone}
                        onChange={(e) =>
                          formik.setFieldValue('phone', normalizeIndianPhoneDigits(e.target.value))
                        }
                        onBlur={formik.handleBlur}
                        inputMode="numeric"
                        maxLength={10}
                        placeholder="10 digit number"
                        className="h-full min-h-0 w-full px-3 text-sm text-gray-900 outline-none"
                      />
                    </div>
                    <FieldError show={showFieldError('phone')} message={formik.errors.phone} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`${formId}-email`} className={LABEL_CLASS}>
                      Email
                    </Label>
                    <Input
                      id={`${formId}-email`}
                      name="email"
                      type="email"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={FIELD_CLASS}
                    />
                    <FieldError show={showFieldError('email')} message={formik.errors.email} />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor={`${formId}-address`} className={LABEL_CLASS}>
                      Address
                    </Label>
                    <Textarea
                      id={`${formId}-address`}
                      name="address"
                      value={formik.values.address}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`${FIELD_CLASS} h-auto min-h-[5rem]`}
                      rows={3}
                    />
                  </div>
                </div>
              </FormSection>

              <FormSection icon={User} title="Nominee" description="Nominee details for membership">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor={`${formId}-nominee-name`} className={LABEL_CLASS}>
                      Nominee Name *
                    </Label>
                    <Input
                      id={`${formId}-nominee-name`}
                      name="nomineeName"
                      value={formik.values.nomineeName}
                      onChange={(e) => formik.setFieldValue('nomineeName', toLettersOnly(e.target.value))}
                      onBlur={formik.handleBlur}
                      placeholder="Enter nominee name"
                      className={FIELD_CLASS}
                    />
                    <FieldError show={showFieldError('nomineeName')} message={formik.errors.nomineeName} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`${formId}-nominee-relationship`} className={LABEL_CLASS}>
                      Nominee Relationship *
                    </Label>
                    <Select
                      value={formik.values.nomineeRelationship || undefined}
                      onValueChange={(value: string) => setSelectValue('nomineeRelationship', value)}
                    >
                      <SelectTrigger id={`${formId}-nominee-relationship`} className={SELECT_FIELD_CLASS}>
                        <SelectValue
                          placeholder="Select relationship"
                          className="text-gray-900 data-[placeholder]:text-muted-foreground"
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {NOMINEE_RELATIONSHIP_OPTIONS.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FieldError
                      show={showFieldError('nomineeRelationship')}
                      message={formik.errors.nomineeRelationship}
                    />
                  </div>
                </div>
              </FormSection>

              <FormSection icon={FileText} title="Documents" description="Upload proof documents (PDF or image)">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {[
                    { id: proof1InputId, slot: proof1Slot, setSlot: setProof1Slot, label: 'Proof Document 1', existing: member?.proof1 },
                    { id: proof2InputId, slot: proof2Slot, setSlot: setProof2Slot, label: 'Proof Document 2', existing: member?.proof2 },
                  ].map((doc) => (
                    <div key={doc.id} className="min-w-0 space-y-2">
                      <Label className={LABEL_CLASS}>{doc.label}</Label>
                      <div className="overflow-hidden rounded-lg border-2 border-dashed border-gray-200 bg-gray-50/50 p-5 text-center transition-colors hover:border-[#4369a0]/30 hover:bg-[#4369a0]/5">
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          disabled={doc.slot.uploading}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleUpload(file, doc.setSlot, uploadFile, doc.id);
                          }}
                          className="hidden"
                          id={doc.id}
                        />
                        <label htmlFor={doc.id} className="block min-w-0 cursor-pointer">
                          <Upload className="mx-auto mb-2 h-8 w-8 text-gray-400" />
                          <p className="px-1 text-sm font-medium text-gray-700" title={doc.slot.name ?? doc.existing}>
                            {doc.slot.uploading
                              ? 'Uploading...'
                              : doc.slot.url && doc.slot.name
                              ? formatFileName(doc.slot.name)
                              : doc.existing
                                ? formatFileName(doc.existing)
                                : 'Click to upload'}
                          </p>
                          <p className="mt-1 text-xs text-gray-500">PDF, JPG or PNG</p>
                        </label>
                        {doc.slot.error && <p className="mt-2 text-xs text-red-600">{doc.slot.error}</p>}
                        {doc.slot.url && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className={`mt-3 ${DELETE_BUTTON_CLASS}`}
                            onClick={() => {
                              doc.setSlot(EMPTY_SLOT);
                              resetFileInput(doc.id);
                            }}
                          >
                            <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                            Remove
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </FormSection>
            </form>
          )}
        </div>

        <DrawerFooter className="shrink-0 flex-row justify-end gap-2 border-t border-gray-200 bg-white px-6 py-4">
          {formik.status && (
            <div className="mr-auto flex min-w-0 flex-1 items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-700">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{formik.status}</span>
            </div>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            className="border-gray-200"
            disabled={formik.isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            className="min-w-28 bg-[#4369a0] text-white hover:bg-[#4369a0]/90"
            disabled={formik.isSubmitting || isUploading}
          >
            {formik.isSubmitting ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                Saving...
              </span>
            ) : isAddMode ? (
              'Add Member'
            ) : (
              'Update'
            )}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
